// Dialog for adding a new taxonomy node (root or child).
// Collects name and optional description.

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'

interface AddNodeDialogProps {
  open: boolean
  parentName: string | null // null = adding root node
  onConfirm: (name: string, description: string) => void
  onCancel: () => void
}

export function AddNodeDialog({ open, parentName, onConfirm, onCancel }: AddNodeDialogProps) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (name.trim()) {
      onConfirm(name.trim(), description.trim())
      setName('')
      setDescription('')
    }
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onCancel()
      setName('')
      setDescription('')
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {parentName ? `Add child under "${parentName}"` : 'Add root node'}
          </DialogTitle>
          <DialogDescription>
            {parentName
              ? 'This node will be added as a child of the selected node.'
              : 'This node will be added at the top level of the taxonomy.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="node-name">Name</Label>
              <Input
                id="node-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Operational Risk"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="node-description">Description (optional)</Label>
              <Input
                id="node-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of this category"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim()}>
              Add Node
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
